import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, useLocation, useNavigate } from "react-router-dom";
import { lazy, Suspense, useEffect } from "react";
import { AnimatePresence, motion } from "framer-motion";
import BottomNav from "@/components/BottomNav";
import SideNav from "@/components/SideNav";
import InstallPrompt from "@/components/InstallPrompt";
import OfflineIndicator from "@/components/OfflineIndicator";
import KeyboardShortcutsOverlay from "@/components/KeyboardShortcuts";
import SkipToContent from "@/components/SkipToContent";
import RouteSeo from "@/components/RouteSeo";
import Index from "./pages/Index";
import { Navigate } from "react-router-dom";

/**
 * Trade pages load on demand so the first open is quick. The service worker
 * precaches every chunk, so they still open with no signal.
 */
const PipeSizing = lazy(() => import("./pages/PipeSizing"));
const Electrical = lazy(() => import("./pages/Electrical"));
const Ductwork = lazy(() => import("./pages/Ductwork"));
const Jobs = lazy(() => import("./pages/Jobs"));
const Trades = lazy(() => import("./pages/Trades"));
const Shop = lazy(() => import("./pages/Shop"));
const Install = lazy(() => import("./pages/Install"));
const Settings = lazy(() => import("./pages/Settings"));
const Structural = lazy(() => import("./pages/Structural"));
const Support = lazy(() => import("./pages/Support"));
const Help = lazy(() => import("./pages/Help"));
const PipeSketch = lazy(() => import("./pages/PipeSketch"));
const NotFound = lazy(() => import("./pages/NotFound"));


const queryClient = new QueryClient();

const pageVariants = {
  initial: { opacity: 0, y: 12 },
  animate: { opacity: 1, y: 0, transition: { duration: 0.25, ease: "easeOut" as const } },
  exit: { opacity: 0, y: -8, transition: { duration: 0.15, ease: "easeIn" as const } },
};

const GlobalSearchShortcut = () => {
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key === "k") {
        e.preventDefault();
        if (location.pathname !== "/") {
          navigate("/?focus=search");
        }
      }
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [navigate, location.pathname]);

  return null;
};

const AnimatedRoutes = () => {
  const location = useLocation();

  return (
    <AnimatePresence mode="wait">
      <motion.div
        key={location.pathname}
        variants={pageVariants}
        initial="initial"
        animate="animate"
        exit="exit"
        className="min-h-screen"
      >
        <Suspense
          fallback={
            <div className="flex min-h-[60vh] items-center justify-center text-sm text-muted-foreground">
              Loading…
            </div>
          }
        >
          <Routes location={location}>
            <Route path="/" element={<Index />} />
            <Route path="/pipe" element={<PipeSizing />} />
            <Route path="/electrical" element={<Electrical />} />
            <Route path="/ductwork" element={<Ductwork />} />
            <Route path="/calculators" element={<Navigate to="/" replace />} />
            <Route path="/structural" element={<Structural />} />
            <Route path="/converter" element={<Navigate to="/" replace />} />
            <Route path="/jobs" element={<Jobs />} />
            <Route path="/trades" element={<Trades />} />
            <Route path="/shop" element={<Shop />} />
            <Route path="/joblog" element={<Navigate to="/jobs?tool=hours" replace />} />
            <Route path="/formulas" element={<Navigate to="/" replace />} />
            <Route path="/install" element={<Install />} />
            <Route path="/settings" element={<Settings />} />
            <Route path="/support" element={<Support />} />
            <Route path="/help" element={<Help />} />
            <Route path="/sketch" element={<PipeSketch />} />
            <Route path="*" element={<NotFound />} />
          </Routes>
        </Suspense>

      </motion.div>
    </AnimatePresence>
  );
};

const App = () => (
  <QueryClientProvider client={queryClient}>
    <TooltipProvider>
      <Toaster />
      <Sonner />
        <BrowserRouter>
          <RouteSeo />
          <GlobalSearchShortcut />
          <KeyboardShortcutsOverlay />
        <div className="min-h-screen bg-background">
          <SkipToContent />
          <OfflineIndicator />
          <SideNav />
           <main id="main-content" className="md:pl-[var(--sidenav-w,15rem)] transition-[padding]" role="main">
            <AnimatedRoutes />
          </main>
          <BottomNav />
          <InstallPrompt />
        </div>
      </BrowserRouter>
    </TooltipProvider>
  </QueryClientProvider>
);

export default App;
